import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { laneApi } from "../api/laneApi";

const SEARCH_MIN_LEN = 2;
const DEBOUNCE_MS = 300;

/**
 * Type-ahead search over an office's real facilities (e.g. lane via points) — lets a user find
 * and reuse an existing Location instead of retyping its address, which both saves typing and
 * avoids near-duplicate Location rows from small address typos.
 *
 * `onSelect(location)` fires with the full Location object; the caller is responsible for
 * populating whatever fields it needs from it. Free typing without selecting a suggestion is
 * still allowed — this augments the existing text input, it doesn't require picking a match.
 *
 * The suggestion list is rendered via a portal to document.body (matching the pattern already
 * used by FilterButton.js/ActionMenu.js) because this field lives inside ExpandableHeader, which
 * clips overflow by default — an absolutely-positioned dropdown inside it would be invisible.
 */
export const FacilityTypeahead = ({ officeCode, value, onChange, onSelect, placeholder }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  const search = useCallback((query) => {
    if (abortRef.current) abortRef.current.abort();
    if (!officeCode || query.trim().length < SEARCH_MIN_LEN) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    laneApi
      .searchFacilities(officeCode, query.trim())
      .then((res) => {
        setSuggestions(res.data || []);
        if (inputRef.current) {
          const rect = inputRef.current.getBoundingClientRect();
          setMenuPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
        }
        setIsOpen(true);
      })
      .catch((err) => {
        console.error("Facility search failed:", err.response?.data || err.message);
        setSuggestions([]);
      })
      .finally(() => setIsLoading(false));
  }, [officeCode]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(value || ""), DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [value, search]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        containerRef.current && !containerRef.current.contains(e.target) &&
        !e.target.closest("[data-facility-menu='true']")
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (location) => {
    setIsOpen(false);
    setSuggestions([]);
    onSelect(location);
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => suggestions.length > 0 && setIsOpen(true)}
        className="mt-1 block w-full px-2 py-1 border border-gray-300 dark:border-acs-dark-border rounded-md text-sm bg-white dark:bg-acs-dark-surface text-gray-900 dark:text-acs-dark-text focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        placeholder={placeholder}
        autoComplete="off"
      />
      {isOpen && (isLoading || suggestions.length > 0) &&
        createPortal(
          <div
            data-facility-menu="true"
            className="fixed z-[9999] bg-white dark:bg-acs-dark-bg border border-gray-200 dark:border-acs-dark-border rounded-md shadow-lg max-h-56 overflow-y-auto"
            style={{ top: menuPos.top, left: menuPos.left, width: Math.max(220, menuPos.width) }}
          >
            {isLoading ? (
              <div className="px-3 py-2 text-xs text-gray-400 dark:text-acs-dark-subtext">Searching...</div>
            ) : (
              suggestions.map((loc) => (
                <button
                  type="button"
                  key={loc.id}
                  onClick={() => handleSelect(loc)}
                  className="block w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-acs-dark-hover border-b border-gray-100 dark:border-acs-dark-border last:border-b-0"
                >
                  <div className="font-medium text-gray-800 dark:text-acs-dark-text">{loc.locationName}</div>
                  <div className="text-xs text-gray-500 dark:text-acs-dark-subtext">
                    {[loc.addressLine1, loc.city, loc.state, loc.zipCode].filter(Boolean).join(", ")}
                  </div>
                </button>
              ))
            )}
          </div>,
          document.body
        )}
    </div>
  );
};
